import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';
import theme from 'src/theme';
import { ReactComponent as BellIconSvg } from 'src/assets/icons/bell.svg';

export const S: { [key: string]: React.CSSProperties } = {
    verticallyCentered: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center'
    },
    statusText: {
        fontSize: 13,
        borderRadius: '16px',
        padding: '8px'
    },
    link: {
        color: theme.color.lightningYellow,
        cursor: 'pointer',
        textDecoration: 'underline'
    }
};

export const tableStyles: { [key: string]: React.CSSProperties } = {
    tableControls: {
        justifyContent: 'flex-end',
        marginBottom: '17px'
    },
    rowView: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center'
    },
    container: {
        backgroundColor: theme.color.darkBlue,
        maxHeight: '683px'
    },
    root: {
        paddingLeft: '100px',
        paddingRight: '100px'
    },
    table: {
        backgroundColor: theme.color.darkBlue,
        minHeight: 350,
        overflowY: 'scroll',
        overflowX: 'hidden'
    },
    headRow: {
        backgroundColor: theme.color.darkBlue,
        borderBottom: `0.5px solid ${theme.color.white30}`,
        position: 'sticky',
        top: 0
    },
    dataRow: {
        display: 'flex'
    },
    baseLabel: {
        display: 'block',
        fontFamily: 'Inter',
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '15px',
        textAlign: 'center',
        padding: 0,
        color: theme.color.white90
    },
    statusCell: {
        fontSize: '9px'
    },
    flagCell: {
        fontSize: '14px'
    },
    headCell: {
        padding: '24px 0'
    },
    firstHeadCell: {
        flexBasis: '180px',
        flexGrow: 1.8
    },
    headCellMd: { flexBasis: '120px', flexGrow: 1.2 },
    headCellLg: { flexBasis: '130px', flexGrow: 1.3 },
    headCellXl: { flexBasis: '150px', flexGrow: 1.5 },
    lastHeadCell: {
        flexBasis: '200px',
        flexGrow: 2
    },
    muiTableCell: {
        display: 'block',
        backgroundColor: theme.color.darkBlue,
        borderBottom: `0.5px solid ${theme.color.white30}`,
        flexBasis: '100px',
        flexGrow: 1
    },
    dataCell: {
        fontWeight: 400,
        lineHeight: '18px',
        color: theme.color.white50
    },
    dataCellPadding: {
        padding: '22px 0'
    },
    pagination: {
        backgroundColor: theme.color.darkBlue,
        color: theme.color.white50,
        borderBottom: 'none'
    },
    paginationContainer: {
        backgroundColor: theme.color.darkBlue,
        padding: '0 20px !important',
        width: '100%'
    },
    caret: {
        color: theme.color.white50
    },
    pointer: {
        cursor: 'pointer'
    }
};

export const DataTableRow = styled(TableRow)`
    display: flex;
`;

export const DataTableCell = styled(TableCell)`
    display: block;
    background-color: ${theme.color.darkBlue};
    border-bottom: 0.5px solid ${theme.color.white30};
    flex-basis: 100px;
    flex-grow: 1;
`;

export const CellContent = styled(Box)`
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    align-items: center;
`;

export const CellLabel = styled('span')`
    display: block;
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 15px;
    text-align: center;
    padding: 0;
    color: ${theme.color.white90};
`;

export const StyledBellIcon = styled(BellIconSvg)`
    width: 13px;
    height: 13px;
    fill: ${theme.color.lightningYellow};
    flex-shrink: 0;
`;

export const TooltipAnchor = styled(Box)`
    display: inline-flex;
`;

export const ClickableBadgeWrapper = styled('span')`
    cursor: pointer;
`;

export const TitleCellContent = styled(Box)`
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 100%;
    gap: 4px;
`;

export const TickerButton = styled(Box)`
    display: flex;
    align-items: center;
    cursor: pointer;
    flex-shrink: 0;
`;

export const TickerArrowRight = styled(KeyboardArrowRight)`
    color: ${theme.color.white50};
    font-size: 18px !important;
`;

export const TickerArrowDown = styled(KeyboardArrowDown)`
    color: ${theme.color.white50};
    font-size: 18px !important;
`;

export const QuestTitleLabel = styled(Typography)`
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 15px;
    color: ${theme.color.white90};
    cursor: pointer;
`;

export const BellWrapper = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    cursor: pointer;
    flex-shrink: 0;
`;

export const BellDataTableCell = styled(TableCell)`
    display: block;
    background-color: ${theme.color.darkBlue};
    border-bottom: 0.5px solid ${theme.color.white30};
    flex-basis: 30px;
    flex-grow: 0;
    min-width: 30px;
    max-width: 30px;
`;

export const TasksExpandCell = styled(TableCell)`
    padding: 0 !important;
    border-bottom: none !important;
    background-color: ${theme.color.darkBlue};
`;

export const TaskListBox = styled(Box)`
    padding: 8px 24px 16px 48px;
    background-color: ${theme.color.blackRock};
    border-bottom: 0.5px solid ${theme.color.white10};
`;

export const TaskRow = styled(Box)`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: ${theme.color.white07};
    }
`;

export const TaskTitle = styled(Typography)`
    font-size: 12px;
    font-family: Inter, sans-serif;
    color: ${theme.color.white80};
`;

export const TaskStatusBadge = styled(Box)<{
    badgebg: string;
    badgecolor: string;
}>`
    font-size: 11px;
    font-family: Inter, sans-serif;
    white-space: nowrap;
    padding: 2px 8px;
    border-radius: 10px;
    background-color: ${(props) => props.badgebg};
    color: ${(props) => props.badgecolor};
`;
